import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, Button, Space, Tag, Modal, message, 
  Input, Select, Form, Tabs 
} from 'antd';
import { 
  EditOutlined, DeleteOutlined, EyeOutlined,
  CheckCircleOutlined, CloseCircleOutlined
  // UserAddOutlined // Not used
} from '@ant-design/icons';
import axios from 'axios';
import apiService from '../services/apiService';

const { Search } = Input;
const { TabPane } = Tabs;

const Organizers = () => {
  const [organizers, setOrganizers] = useState([]);
  const [pendingOrganizers, setPendingOrganizers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedOrganizer, setSelectedOrganizer] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'createdAt',
    order: 'desc'
  });

  const fetchOrganizers = async (params = {}) => {
    setLoading(true);
    try {
      const { current, pageSize } = params;
      const { search, sortBy, order } = filters;

      const response = await axios.get(
        apiService.admin.getOrganizers({ 
          page: current, 
          limit: pageSize, 
          sortBy, 
          order, 
          search 
        }), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setOrganizers(response.data.organizers);
      setPagination({
        ...pagination,
        total: response.data.total,
        current: response.data.currentPage
      });
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors du chargement des organisateurs');
    }
    setLoading(false);
  };

  const fetchPendingOrganizers = async () => {
    try {
      const response = await axios.get(
        apiService.admin.getPendingOrganizers(),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setPendingOrganizers(response.data.pendingOrganizers);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors du chargement des demandes en attente');
    }
  };

  useEffect(() => {
    fetchOrganizers({ current: 1, pageSize: 10 });
    fetchPendingOrganizers();
  }, [filters]);

  const handleTableChange = (newPagination, filters, sorter) => {
    const newFilters = { ...filters };
    if (sorter.field && sorter.order) {
      newFilters.sortBy = sorter.field;
      newFilters.order = sorter.order === 'descend' ? 'desc' : 'asc';
    }
    setFilters(newFilters);
    fetchOrganizers(newPagination);
  };

  const handleDelete = async (organizerId) => {
    try {
      await axios.delete(
        apiService.admin.deleteOrganizer(organizerId),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      message.success('Organisateur supprimé avec succès');
      fetchOrganizers(pagination);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de la suppression');
    }
  };

  const handleValidatePending = async (organizerId, approved) => {
    try {
      await axios.post(
        apiService.admin.validateOrganizer(organizerId),
        { approved },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      message.success(approved ? 'Organisateur validé avec succès' : 'Demande rejetée');
      fetchPendingOrganizers();
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de la validation');
    }
  };

  const showModal = (organizer = null) => {
    setSelectedOrganizer(organizer);
    if (organizer) {
      form.setFieldsValue({
        ...organizer,
        ...organizer.user
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleModalSubmit = async (values) => {
    try {
      if (selectedOrganizer) {
        await axios.put(
          apiService.admin.updateOrganizer(selectedOrganizer._id),
          values,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        message.success('Organisateur mis à jour avec succès');
      }

      setIsModalVisible(false);
      fetchOrganizers(pagination);
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur lors de l\'enregistrement');
    }
  };

  const organizerColumns = [
    {
      title: 'Société',
      dataIndex: 'company',
      key: 'company',
      sorter: true,
      render: (company) => (
        <span className="font-medium text-neutral-900">{company}</span>
      ),
    },
    {
      title: 'Nom',
      dataIndex: 'lastName',
      key: 'lastName',
      sorter: true,
    },
    {
      title: 'Email',
      dataIndex: ['user', 'email'],
      key: 'email',
    },
    {
      title: 'Téléphone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Langue',
      dataIndex: 'language',
      key: 'language',
      render: (language) => (
        <Tag color="blue" className="rounded-full px-3 py-0.5">
          {language}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EyeOutlined />}
            onClick={() => showModal(record)}
            className="hover:text-primary-600 hover:border-primary-600 transition-colors"
          />
          <Button 
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            className="hover:text-primary-600 hover:border-primary-600 transition-colors"
          />
          <Button 
            icon={<DeleteOutlined />}
            danger
            onClick={() => {
              Modal.confirm({
                title: 'Êtes-vous sûr de vouloir supprimer cet organisateur ?',
                content: 'Cette action est irréversible.',
                okText: 'Oui',
                cancelText: 'Non',
                onOk: () => handleDelete(record._id),
                className: 'shadow-elevation-2'
              });
            }}
            className="hover:bg-error-50"
          />
        </Space>
      ),
    },
  ];

  const pendingColumns = [
    {
      title: 'Société',
      dataIndex: 'company',
      key: 'company',
    },
    {
      title: 'Nom',
      dataIndex: 'lastName',
      key: 'lastName',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Téléphone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => handleValidatePending(record._id, true)}
            className="bg-success-600 hover:bg-success-700"
          >
            Valider
          </Button>
          <Button
            danger
            icon={<CloseCircleOutlined />}
            onClick={() => handleValidatePending(record._id, false)}
          >
            Rejeter
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6 p-6 bg-gradient-to-b from-neutral-50 to-white min-h-screen">
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <h1 className="text-heading-2 font-heading font-semibold text-neutral-900">
            Gestion des Organisateurs
          </h1>
          <p className="text-neutral-500 text-body-lg">
            Gérez les organisateurs et leurs demandes d'inscription
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-elevation-1 overflow-hidden border border-neutral-200">
        <Tabs defaultActiveKey="1" className="px-6 pt-4">
          <TabPane tab="Organisateurs actifs" key="1">
            <div className="mb-4">
              <Search
                placeholder="Rechercher un organisateur"
                onSearch={(value) => setFilters({ ...filters, search: value })}
                className="w-72 shadow-sm"
                allowClear
              />
            </div>
            <Table
              columns={organizerColumns}
              dataSource={organizers}
              rowKey="_id"
              pagination={{
                ...pagination,
                className: "px-6 py-4 bg-neutral-50 border-t border-neutral-200",
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} organisateurs`,
              }}
              loading={loading}
              onChange={handleTableChange}
              className="ant-table-custom"
              rowClassName="hover:bg-neutral-50 transition-colors"
            />
          </TabPane>
          <TabPane 
            tab={
              <span>
                Demandes en attente
                {pendingOrganizers.length > 0 && (
                  <Tag className="ml-2 rounded-full bg-warning-50 border-warning-200 text-warning-700">
                    {pendingOrganizers.length}
                  </Tag>
                )}
              </span>
            } 
            key="2"
          >
            <Table
              columns={pendingColumns}
              dataSource={pendingOrganizers}
              rowKey="_id"
              pagination={false}
              className="ant-table-custom"
              rowClassName="hover:bg-neutral-50 transition-colors"
            />
          </TabPane>
        </Tabs>
      </div>

      <Modal
        title={
          <div className="space-y-1 pb-3 border-b border-neutral-200">
            <h3 className="text-heading-4 font-heading font-semibold text-neutral-900">
              {selectedOrganizer ? 'Modifier l\'organisateur' : 'Détails de l\'organisateur'}
            </h3>
          </div>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
        className="shadow-elevation-3"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleModalSubmit}
          className="space-y-6 pt-6"
        >
          <Form.Item
            name="company"
            label="Société"
            rules={[{ required: true, message: 'La société est requise' }]}
          >
            <Input className="rounded-lg shadow-sm" />
          </Form.Item>

          <Space className="w-full justify-between">
            <Form.Item
              name="lastName"
              label="Nom"
              rules={[{ required: true, message: 'Le nom est requis' }]}
            >
              <Input className="rounded-lg shadow-sm" />
            </Form.Item>

            <Form.Item
              name="name"
              label="Prénom"
              rules={[{ required: true, message: 'Le prénom est requis' }]}
            >
              <Input className="rounded-lg shadow-sm" />
            </Form.Item>
          </Space>

          <Space className="w-full justify-between">
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'L\'email est requis' },
                { type: 'email', message: 'Email invalide' }
              ]}
            >
              <Input className="rounded-lg shadow-sm" />
            </Form.Item>

            <Form.Item
              name="phone"
              label="Téléphone"
              rules={[{ required: true, message: 'Le téléphone est requis' }]}
            >
              <Input className="rounded-lg shadow-sm" />
            </Form.Item>
          </Space>

          <Form.Item
            name="language"
            label="Langue"
            rules={[{ required: true, message: 'La langue est requise' }]}
          >
            <Select className="rounded-lg shadow-sm">
              <Select.Option value="fr">Français</Select.Option>
              <Select.Option value="en">Anglais</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item className="mb-0 pt-4 border-t border-neutral-200">
            <div className="flex justify-end gap-3">
              <Button 
                onClick={() => setIsModalVisible(false)}
                className="hover:bg-neutral-50 transition-colors px-6"
              >
                Fermer
              </Button>
              {selectedOrganizer && (
                <Button 
                  type="primary" 
                  htmlType="submit"
                  className="bg-primary-600 hover:bg-primary-700 shadow-sm hover:shadow-md transition-all px-6"
                >
                  Enregistrer les modifications
                </Button>
              )}
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Organizers; 